import { useQuery } from '@tanstack/react-query';
import { Dispatch } from 'redux';

import { requestHandler } from '../../api';
import {
  CessionsResponse,
  CompanyLoginStatus,
  ExecutiveReponse,
} from './types';

export const getAutoEnrollmentStatus = async (
  dispatch: any,
  clientId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${clientId}/getAutoEnrollmentStatus`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCompanyLoginStatus = (dispatch: Dispatch, clientId?: string) =>
  useQuery({
    queryKey: ['companyLoginStatus', clientId],
    queryFn: async (): Promise<CompanyLoginStatus | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/express/${clientId}/companyValidationForLoggingIn`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: !!clientId,
    staleTime: 1000 * 60 * 60 * 12,
    gcTime: 1000 * 60 * 60 * 12,
    retry: false,
  });

export const getCompanyDetails = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/details`,
    dispatch,
  });
  if (response) return response.data;
};

export const getCertificates = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/settings/certificates`,
    dispatch,
  });
  if (response) return response.data;
};

export const getSelectedCertificate = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/settings/certificates/selected`,
    dispatch,
  });
  if (response) return response.data;
};

export const addCertificates = async (
  dispatch: any,
  companyRecieverId: string,
  data: any,
) => {
  const headers = {
    'Content-Type': 'multipart/form-data',
  };
  const response = await requestHandler({
    method: 'post',
    headers,
    data,
    url: `internal/express/${companyRecieverId}/settings/certificates`,
    dispatch,
  });
  if (response) return response.data;
};

export const deleteCertificate = async (
  dispatch: any,
  companyRecieverId: string,
  id: number,
) => {
  const response = await requestHandler({
    method: 'delete',
    url: `internal/express/${companyRecieverId}/settings/certificates/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const selectCertificate = async (
  dispatch: any,
  companyRecieverId: string,
  id: number,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/express/${companyRecieverId}/settings/certificates/${id}/select`,
    dispatch,
  });
  if (response) return response.data;
};

export const getExpressFactoringRequestsSent = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/factoring/request`,
    dispatch,
  });
  if (response) return response.data;
};

export const getExpressFactoringRequestsSentDetails = async (
  dispatch: any,
  companyRecieverId: string,
  id: number,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/factoring/request/${id}`,
    dispatch,
  });
  if (response) return response.data;
};

export const createExpressFactoringRequest = async (
  dispatch: any,
  companyRecieverId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/express/${companyRecieverId}/factoring/request`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const approveExpressFactoringRequest = async (
  dispatch: any,
  companyRecieverId: string,
  id: number,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/express/${companyRecieverId}/factoring/request/${id}/approve`,
    dispatch,
  });
  if (response) return response.data;
};

export const executeCession = async (
  dispatch: any,
  companyRecieverId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/express/${companyRecieverId}/factoring/request/cession/execute`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const executeCessionSII = async (
  dispatch: any,
  companyRecieverId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/express/${companyRecieverId}/factoring/request/cession/executeSII`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getStatusOfRelationBetweenClientAndReceiver = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/enrollment/status`,
    dispatch,
  });
  if (response) return response.data;
};

export const activateRelationBetweenReceiverAndReceiver = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/express/${companyRecieverId}/enrollment/activate`,
    dispatch,
  });
  if (response) return response.data;
};

export const deactivateRelationBetweenReceiverAndReceiver = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/express/${companyRecieverId}/enrollment/deactivate`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBlockedInvoices = async (
  dispatch: any,
  companyRecieverId: string,
  folios: string[],
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/factoring/blockedInvoices?folios=${folios.join(',')}`,
    dispatch,
  });
  if (response) return response.data;
};

export const expressGetCompanyLogo = async (
  dispatch: any,
  companyRecieverId: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/factoring/companyLogo`,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessSummaryExpress = async (
  dispatch: any,
  companyRecieverId: string,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/business/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const updateBusinessSummaryExpress = async (
  dispatch: any,
  companyRecieverId: string,
  businessIdentifier: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'patch',
    url: `internal/express/${companyRecieverId}/business/${businessIdentifier}`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const createBussinessExpress = async (
  dispatch: any,
  companyRecieverId: string,
  data: any,
) => {
  const response = await requestHandler({
    method: 'post',
    url: `internal/express/${companyRecieverId}/business`,
    data,
    dispatch,
  });
  if (response) return response.data;
};

export const getBusinessByIdentifierExpress = async (
  dispatch: any,
  companyRecieverId: string,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/business/identifier/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const searchOrCreateBusinessExpress = async (
  dispatch: Dispatch,
  companyRecieverId: string,
  businessIdentifier: string,
) => {
  const response = await requestHandler({
    method: 'put',
    url: `internal/express/${companyRecieverId}/business/searchOrCreate/${businessIdentifier}`,
    dispatch,
  });
  if (response) return response.data;
};

export const getAssignatedExecutives = (
  dispatch: Dispatch,
  companyRecieverId?: string,
  businessIdentifier?: string,
) =>
  useQuery({
    queryKey: ['assignatedExecutives', companyRecieverId, businessIdentifier],
    queryFn: async (): Promise<ExecutiveReponse[] | undefined> => {
      const response = await requestHandler({
        method: 'get',
        url: `internal/express/${companyRecieverId}/business/${businessIdentifier}/executives`,
        dispatch,
      });
      if (response) return response.data;
    },
    enabled: !!companyRecieverId && !!businessIdentifier,
    staleTime: 1000 * 60 * 60 * 12,
    gcTime: 1000 * 60 * 60 * 12,
    retry: false,
  });

export const getBusinessCessionsExpress = async (
  dispatch: Dispatch,
  query: string,
  companyRecieverId: string,
): Promise<CessionsResponse | undefined> => {
  const response = await requestHandler({
    method: 'get',
    url: `internal/express/${companyRecieverId}/business/cessions?${query}`,
    dispatch,
  });
  if (response) return response.data;
};
